import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {CheckboxInputGrid, MappingState} from 'Quoting/components/shared/CheckboxInputGrid';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getParentStructurePath} from '../Common/PropertyPaths';
import {getOutPatientPhysicianTreatment} from './PropertyPaths/propertyPathACC';

const Portability = () => {
  const rows: GridRowProps[] = [];
  return <FormRowGrid rows={rows} />;
};

const OutpatientPhysicianTreatements = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const coverageIndex = 0;
  const propertyPaths = getOutPatientPhysicianTreatment(selectedPlan?.planIndex ?? 0, 0);

  const benefitName = 'outpatientPhysicianTreatmentsDetails';

  const getExclusionPath = (benefitName: string) => {
    return getBenefitLabelMapping(
      `${benefitName}Exclusions`,
      `${getParentStructurePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
        section: 'outpatient',
      }
    );
  };
  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'out-patient-physician-treatement-row-1'},
      items: [
        {
          key: 'out-patient-physician-treatement-type-of-rider',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Type of Rider"
              dropdownVariant="AttributeValue"
              searchKey={`ACCOutpatientPhysicianTreatments.typeOfRider`}
              value={getQuoteFieldValue(propertyPaths.outpatientPhysicianTreatmentsDetails.typeOfRider)}
              onChange={e =>
                updateQuoteFieldValue(propertyPaths.outpatientPhysicianTreatmentsDetails.typeOfRider, e.target.value)
              }
              id={propertyPaths.outpatientPhysicianTreatmentsDetails.typeOfRider}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'out-patient-physician-treatement-units',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Units"
              value={getQuoteFieldValue<number>(propertyPaths.outpatientPhysicianTreatmentsDetails.units)}
              onChange={e =>
                updateQuoteFieldValue(propertyPaths.outpatientPhysicianTreatmentsDetails.units, e.target.value)
              }
              id={propertyPaths.outpatientPhysicianTreatmentsDetails.units}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'out-patient-physician-treatement-row-2'},
      items: [
        {
          key: 'out-patient-physician-treatement-days',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Maximum Number of Days (Per Covered Person)"
              value={getQuoteFieldValue<number>(propertyPaths.outpatientPhysicianTreatmentsDetails.maxBenefitNumber)}
              onChange={e =>
                updateQuoteFieldValue(
                  propertyPaths.outpatientPhysicianTreatmentsDetails.maxBenefitNumber,
                  e.target.value
                )
              }
              id={propertyPaths.outpatientPhysicianTreatmentsDetails.maxBenefitNumber}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'out-patient-physician-treatement-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`ACCOutpatientPhysicianTreatments.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.outpatientPhysicianTreatmentsDetails.timePeriodCd)}
              onChange={e =>
                updateQuoteFieldValue(propertyPaths.outpatientPhysicianTreatmentsDetails.timePeriodCd, e.target.value)
              }
              id={propertyPaths.outpatientPhysicianTreatmentsDetails.timePeriodCd}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'out-patient-physician-treatement-row-2'},
      items: [
        {
          key: 'out-patient-physician-treatement-time-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Daily Benefit Amount (Per Unit)"
              value={getQuoteFieldValue<number>(propertyPaths.outpatientPhysicianTreatmentsDetails.benefitAmount)}
              onChange={e =>
                updateQuoteFieldValue(propertyPaths.outpatientPhysicianTreatmentsDetails.benefitAmount, e.target.value)
              }
              id={propertyPaths.outpatientPhysicianTreatmentsDetails.benefitAmount}
              adornment="money"
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return (
    <>
      <FormRowGrid rows={rows} />
      <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
        <CheckboxInputGrid state={exclusionPaths} gridLength={3} hideOnJobAccident={false} />
      </FormWrapper>
    </>
  );
};

export const AdditionalOptionsandBenefitsGVA6Block = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const propertyPaths = getOutPatientPhysicianTreatment(selectedPlan?.planIndex ?? 0, 0);

  const situsStateCd = getQuoteFieldValue<string>('riskStateCd');
  const restrictedStateCds: string[] = ['MI', 'MN', 'WA'];

  const additionalOptAndBenefits: ConditionalSection[] = [
    {
      Component: Portability,
      key: 'Portability',
      disabled: true,
      label: 'PORTABILITY (UNLIMITED)',
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isPortabilityApplied),
    },
    {
      Component: OutpatientPhysicianTreatements,
      hidden: restrictedStateCds.includes(situsStateCd),
      key: 'OutpatientPhysicianTreatements',
      label: 'OUTPATIENT PHYSICIAN TREATMENTS (4 day max for family coverage)',
      disabled: true,
      // isApplied: !restrictedStateCds.includes(situsStateCd),
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isOutpatientPhysicianTreatmentsApplied),
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(propertyPaths.isOutpatientPhysicianTreatmentsApplied, isApplied);
      },
    },
  ];

  return (
    <FormWrapper title="Additional Options and Benefits" collapsible withSeparator>
      <QuoteFormConditionalSection sections={additionalOptAndBenefits} />
    </FormWrapper>
  );
};
